/**
 * Version Display Component for Learning Companion
 * Shows version information in the UI footer or admin panel
 */

import React from 'react';
import { useVersionInfo, formatVersionString, getEnvironmentStyle } from '../utils/versionUtils';

interface VersionDisplayProps {
  variant?: 'footer' | 'detailed' | 'badge';
  className?: string;
}

export const VersionDisplay: React.FC<VersionDisplayProps> = ({ 
  variant = 'footer', 
  className = '' 
}) => {
  const { versionInfo, loading, error } = useVersionInfo();

  if (loading) {
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        Loading version...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-xs text-red-500 ${className}`} title={error}>
        Version unavailable
      </div>
    );
  }

  const versionString = formatVersionString(versionInfo);
  const envStyle = getEnvironmentStyle(versionInfo.build.environment);

  if (variant === 'footer') {
    return (
      <div className={`text-xs text-gray-500 flex items-center gap-2 ${className}`}>
        <span>Learning Companion v{versionInfo.version}</span>
        <span className={`px-2 py-1 rounded text-xs border ${envStyle}`}>
          {versionInfo.build.environment}
        </span>
      </div>
    );
  }

  if (variant === 'badge') {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${envStyle} ${className}`}>
        v{versionString}
      </span>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className={`p-4 bg-white rounded-lg border border-gray-200 ${className}`}>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Version Information</h3>
        
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-600">Version:</span>
            <span className="font-mono">{versionString}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Release:</span>
            <span>{versionInfo.release.name}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Environment:</span>
            <span className={`px-2 py-0.5 rounded text-xs border ${envStyle}`}>
              {versionInfo.build.environment}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Build:</span>
            <span className="font-mono">#{versionInfo.build.number}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Built:</span>
            <span>{new Date(versionInfo.build.date).toLocaleDateString()}</span>
          </div>
          
          {versionInfo.build.commit && versionInfo.build.commit !== 'unknown' && (
            <div className="flex justify-between">
              <span className="text-gray-600">Commit:</span>
              <span className="font-mono text-xs">
                {versionInfo.build.commit.substring(0, 7)}
              </span>
            </div>
          )}
          
          <div className="flex justify-between">
            <span className="text-gray-600">API Version:</span>
            <span>{versionInfo.compatibility.api_version}</span>
          </div>
        </div>

        {versionInfo.release.notes && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <h4 className="text-xs font-medium text-gray-700 mb-1">Release Notes</h4>
            <p className="text-xs text-gray-600">{versionInfo.release.notes}</p>
          </div>
        )}

        {versionInfo.release.features.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <h4 className="text-xs font-medium text-gray-700 mb-1">New Features</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              {versionInfo.release.features.slice(0, 3).map((feature, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-green-500 mt-0.5">•</span>
                  <span>{feature}</span>
                </li>
              ))}
              {versionInfo.release.features.length > 3 && (
                <li className="text-gray-400 italic">
                  +{versionInfo.release.features.length - 3} more features...
                </li>
              )}
            </ul>
          </div>
        )}

        {versionInfo.release.bug_fixes.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <h4 className="text-xs font-medium text-gray-700 mb-1">Bug Fixes</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              {versionInfo.release.bug_fixes.slice(0, 2).map((fix, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>{fix}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default VersionDisplay;

// Ensure this file is treated as a module for TypeScript isolatedModules
export {};